import React, { useState } from "react";
import AddStudentModal from "./AddStudentModal";
import { FaCloudUploadAlt } from "react-icons/fa";
import { FiX, FiUser, FiUsers, FiDownload } from "react-icons/fi";
import { userRequest } from "../../utils/requestMethods";

const AddStudentDetailsModal = ({ closeModal, onStudentCreated, schoolCode, schoolId, classId }) => {
  const [screen, setScreen] = useState("main");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const navigateTo = (targetScreen) => setScreen(targetScreen);

  const handleFileChange = (e) => {
    if (e.target.files?.[0]) setFile(e.target.files[0]);
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = "/templates/student-upload-template.xlsx";
    link.setAttribute("download", "student-upload-template.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const uploadHandler = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Please select a file to upload.");
      setTimeout(() => setError(""), 5000);
      return;
    }
    setLoading(true);
    try {
      const studentData = new FormData();
      studentData.append("file", file);
      studentData.append("school_code", schoolCode);
      studentData.append("class_id", classId);
      await userRequest.post("/children/upload", studentData);
      setSuccess(true);
      onStudentCreated();
      closeModal();
    } catch (err) {
      setError(err.response?.data || "Upload failed. Please try again.");
      setTimeout(() => setError(""), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            {screen !== "main" && (
              <button
                onClick={() => navigateTo("main")}
                className="text-xs text-indigo-600 font-medium mb-0.5 flex items-center gap-1 hover:underline"
              >
                ← Back
              </button>
            )}
            <h2 className="text-lg font-bold text-gray-900">
              {screen === "main" && "Add Student"}
              {screen === "manualUpload" && "Student Details"}
              {screen === "bulkUploads" && "Bulk Upload"}
            </h2>
          </div>
          <button
            onClick={closeModal}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5">

          {/* Main Screen */}
          {screen === "main" && (
            <div className="space-y-3">
              <p className="text-sm text-gray-500 mb-4">Choose how you'd like to add students to this class.</p>

              <button
                onClick={() => navigateTo("manualUpload")}
                className="w-full flex items-center gap-4 p-4 bg-indigo-50 border border-indigo-100 rounded-xl hover:bg-indigo-100 hover:border-indigo-200 transition-all group text-left"
              >
                <div className="w-10 h-10 rounded-xl bg-indigo-600 flex items-center justify-center flex-shrink-0 group-hover:bg-indigo-700 transition-colors">
                  <FiUser size={18} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">Manual Entry</p>
                  <p className="text-xs text-gray-500 mt-0.5">Create a profile for one student at a time</p>
                </div>
                <span className="text-gray-400 group-hover:text-indigo-600 text-lg transition-colors">→</span>
              </button>

              <button
                onClick={() => navigateTo("bulkUploads")}
                className="w-full flex items-center gap-4 p-4 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 hover:border-gray-300 transition-all group text-left"
              >
                <div className="w-10 h-10 rounded-xl bg-gray-700 flex items-center justify-center flex-shrink-0 group-hover:bg-gray-800 transition-colors">
                  <FiUsers size={18} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">Bulk Upload</p>
                  <p className="text-xs text-gray-500 mt-0.5">Upload multiple students via Excel or CSV file</p>
                </div>
                <span className="text-gray-400 group-hover:text-gray-700 text-lg transition-colors">→</span>
              </button>
            </div>
          )}

          {/* Manual Upload Screen */}
          {screen === "manualUpload" && (
            <AddStudentModal
              goback={() => navigateTo("main")}
              closeModal={closeModal}
              onStudentCreated={onStudentCreated}
              schoolId={schoolId}
              schoolCode={schoolCode}
              classId={classId}
            />
          )}

          {/* Bulk Upload Screen */}
          {screen === "bulkUploads" && (
            <div className="space-y-4">
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <span className="font-semibold">Error:</span> {error}
                </div>
              )}

              <p className="text-sm text-gray-500 leading-relaxed">
                Download the template, fill in your student data, then upload it below.
              </p>

              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-xl hover:bg-indigo-100 transition-colors"
              >
                <FiDownload size={15} />
                Download Template
              </button>

              <form onSubmit={uploadHandler} className="space-y-4">
                <label
                  htmlFor="file-upload"
                  className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-colors"
                >
                  <FaCloudUploadAlt className="text-gray-400 text-4xl mb-3" />
                  <p className="text-sm text-gray-600">
                    <span className="text-indigo-600 font-medium">Click to upload</span> or drag and drop
                  </p>
                  <p className="text-xs text-gray-400 mt-1">.XLSX, .XLS only</p>
                  <input
                    id="file-upload"
                    type="file"
                    onChange={handleFileChange}
                    className="hidden"
                    accept=".xlsx,.xls"
                  />
                </label>

                {file && (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-xl text-sm">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <FiUsers size={14} className="text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-green-800">{file.name}</p>
                      <p className="text-green-600 text-xs">{(file.size / 1024).toFixed(2)} KB</p>
                    </div>
                  </div>
                )}

                <div className="flex justify-end pt-1">
                  <button
                    type="submit"
                    disabled={loading || !file}
                    className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors"
                  >
                    {loading ? "Uploading..." : "Upload Students"}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddStudentDetailsModal;
